import * as React from "react"

import { cn } from "@/lib/utils"

// ─── Canonical dark-mode table tokens ─────────────────────────────────────────
// Single source of truth for table styling (light + dark). Every table widget in
// the app — whether built on a real <table> tag or a CSS `display:table` div grid —
// should consume the components/constants below instead of hand-rolling its own
// className strings, so a style change here propagates everywhere.
//
// Do NOT hardcode `dark:bg-[#0C3451]` / `dark:bg-[#3C5C73]` / `dark:border-*` as
// table row/header/cell colors in widget files — import the tokens or components
// from this file instead. (Non-table uses of these brand colors, e.g. a plain
// Card background, are fine and out of scope for this rule.)

export interface TableColumnConfig {
  title: string
  key: string
  width: number
  filterable?: boolean
}

export const TABLE_CARD_CLASS = "h-full flex flex-col dark:border-primary-fixed"

export const TABLE_HEADER_ROW_COLOR_CLASS = "bg-gray-50 dark:bg-[#3C5C73] border-b border-gray-300 dark:border-0"

export const TABLE_HEADER_ROW_CLASS = cn("sticky top-0 z-30", TABLE_HEADER_ROW_COLOR_CLASS)

export const TABLE_HEADER_CELL_CLASS = "h-[45px] px-4 text-left align-middle font-medium"

export const TABLE_HEADER_CELL_COLOR_CLASS = "bg-gray-50 dark:bg-[#3C5C73]"

export const TABLE_HEADER_CELL_STICKY_CLASS = "sticky left-0 z-20"

export const TABLE_HEADER_TITLE_CLASS = "text-primary dark:text-white font-medium"

export const TABLE_ROW_BASE_CLASS = "transition-colors h-[45px] border-b border-gray-300 dark:border-0"

export function tableRowZebraClass(index: number): string {
  return index % 2 === 0 ? "bg-white dark:bg-card" : "bg-[#fafafa] dark:bg-[#3C5C73]"
}

export function tableRowClass(index: number, className?: string): string {
  return cn(TABLE_ROW_BASE_CLASS, tableRowZebraClass(index), className)
}

export const TABLE_CELL_CLASS = "px-4 align-middle"

export function tableStickyCellClass(index: number, className?: string): string {
  return cn(TABLE_CELL_CLASS, "sticky left-0 z-10", tableRowZebraClass(index), className)
}

export const METRIC_BAR_TRACK_CLASS = "h-[10px] bg-gray-200 dark:bg-white/30 rounded-md overflow-hidden"

// ─── Real <table> primitives ───────────────────────────────────────────────────
// For widgets that render a literal <table>/<thead>/<tbody> tree.

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  // Generic wrapper — the header row/cell is supplied by consumers via TableHeader/TableHeaderCell.
  <table // NOSONAR typescript:S5256
    ref={ref}
    className={cn("w-full table-fixed caption-bottom text-xs border-collapse", className)}
    {...props}
  />
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn(TABLE_HEADER_ROW_CLASS, className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableHeaderRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr ref={ref} className={cn("border-b transition-colors", className)} {...props} />
))
TableHeaderRow.displayName = "TableHeaderRow"

interface TableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sticky?: boolean
}

const TableHeaderCell = React.forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  ({ className, sticky, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(TABLE_HEADER_CELL_CLASS, TABLE_HEADER_CELL_COLOR_CLASS, sticky && TABLE_HEADER_CELL_STICKY_CLASS, className)}
      {...props}
    />
  )
)
TableHeaderCell.displayName = "TableHeaderCell"

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  index?: number
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, index = 0, ...props }, ref) => (
    <tr ref={ref} className={tableRowClass(index, className)} {...props} />
  )
)
TableRow.displayName = "TableRow"

// Back-compat alias for the shadcn-style name.
const TableHead = TableHeaderCell

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  index?: number
  sticky?: boolean
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, index = 0, sticky, ...props }, ref) => (
    <td
      ref={ref}
      className={sticky ? tableStickyCellClass(index, className) : cn(TABLE_CELL_CLASS, className)}
      {...props}
    />
  )
)
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
))
TableCaption.displayName = "TableCaption"

// Wraps the horizontally/vertically scrollable region around a <Table>. Shared
// across every real-<table> widget so the scrollbar look stays identical.
interface TableScrollContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  scrollY?: boolean
}

const TableScrollContainer = React.forwardRef<HTMLDivElement, TableScrollContainerProps>(
  ({ className, scrollY = true, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "w-full overflow-x-auto flex-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 [&::-webkit-scrollbar-corner]:bg-transparent",
        scrollY ? "overflow-y-auto" : "overflow-y-hidden",
        className
      )}
      {...props}
    />
  )
)
TableScrollContainer.displayName = "TableScrollContainer"

// A skeleton loading row for a real <table>. `columns` drives per-cell width +
// the first-column sticky treatment, matching a real TableRow's shape.
interface TableSkeletonRowProps {
  columns: TableColumnConfig[]
}

function TableSkeletonRow({ columns }: Readonly<TableSkeletonRowProps>) {
  return (
    <tr className="transition-colors h-[45px]">
      {columns.map((col, colIdx) => (
        <td
          key={col.key}
          className={cn("p-4 align-middle", colIdx === 0 && "sticky left-0 z-10 bg-gray-50 dark:bg-[#3C5C73]")}
          style={{ width: col.width }}
        >
          <div className="h-4 w-full bg-gray-200 dark:bg-white/30 animate-pulse rounded" />
        </td>
      ))}
    </tr>
  )
}

// An empty/error state row spanning every column of a real <table>.
interface TableEmptyRowProps {
  colSpan: number
  height?: number
  children: React.ReactNode
}

function TableEmptyRow({ colSpan, height = 285, children }: Readonly<TableEmptyRowProps>) {
  return (
    <tr>
      <td colSpan={colSpan} className="p-0" style={{ height }}>
        <div
          className="flex items-center justify-center text-sm text-foreground dark:text-white/60"
          style={{ height }}
        >
          {children}
        </div>
      </td>
    </tr>
  )
}

// ─── CSS `display:table` div-grid primitives ───────────────────────────────────
// For widgets that can't use a real <table> (sticky columns / custom layout
// needs), styled identically to the real-<table> primitives above.

const TableGridHeaderRow = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("table-row", TABLE_HEADER_ROW_CLASS, className)} {...props} />
  )
)
TableGridHeaderRow.displayName = "TableGridHeaderRow"

interface TableGridHeaderCellProps extends React.HTMLAttributes<HTMLDivElement> {
  sticky?: boolean
}

const TableGridHeaderCell = React.forwardRef<HTMLDivElement, TableGridHeaderCellProps>(
  ({ className, sticky, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("table-cell", TABLE_HEADER_CELL_CLASS, sticky && TABLE_HEADER_CELL_STICKY_CLASS, className)}
      {...props}
    />
  )
)
TableGridHeaderCell.displayName = "TableGridHeaderCell"

interface TableGridRowProps extends React.HTMLAttributes<HTMLDivElement> {
  index?: number
}

const TableGridRow = React.forwardRef<HTMLDivElement, TableGridRowProps>(
  ({ className, index = 0, ...props }, ref) => (
    <div ref={ref} className={cn("table-row", tableRowClass(index), className)} {...props} />
  )
)
TableGridRow.displayName = "TableGridRow"

interface TableGridCellProps extends React.HTMLAttributes<HTMLDivElement> {
  index?: number
  sticky?: boolean
}

const TableGridCell = React.forwardRef<HTMLDivElement, TableGridCellProps>(
  ({ className, index = 0, sticky, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("table-cell", sticky ? tableStickyCellClass(index, className) : cn(TABLE_CELL_CLASS, className))}
      {...props}
    />
  )
)
TableGridCell.displayName = "TableGridCell"

interface TableGridSkeletonRowProps {
  columns: TableColumnConfig[]
}

function TableGridSkeletonRow({ columns }: Readonly<TableGridSkeletonRowProps>) {
  return (
    <div className="table-row transition-colors h-[45px]">
      {columns.map((col, colIdx) => (
        <div
          key={col.key}
          className={cn("table-cell p-4 align-middle", colIdx === 0 && "sticky left-0 z-10 bg-gray-50 dark:bg-[#3C5C73]")}
          style={{ width: col.width }}
        >
          <div className="h-4 w-full bg-gray-200 dark:bg-white/30 animate-pulse rounded" />
        </div>
      ))}
    </div>
  )
}

interface TableGridEmptyRowProps {
  height?: number
  children: React.ReactNode
}

function TableGridEmptyRow({ height = 285, children }: Readonly<TableGridEmptyRowProps>) {
  return (
    <div className="table-row">
      <div className="table-cell p-0" style={{ height }}>
        <div
          className="flex items-center justify-center text-sm text-foreground dark:text-white/60"
          style={{ height }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

// ─── Metric / progress bar ─────────────────────────────────────────────────────
// Single implementation shared by every table cell that renders a value + a
// horizontal progress track (SLA %, transferred rate, AHT, gauge cells, ...).

export interface MetricBarProps {
  value: number
  pct: number
  displayText: string
  barClassName?: string
  labelClassName?: string
}

function MetricBar({ value, pct, displayText, barClassName = "bg-action dark:bg-info", labelClassName }: Readonly<MetricBarProps>) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className={cn("w-[50px] shrink-0", METRIC_BAR_TRACK_CLASS)} aria-hidden>
        {value > 0 ? <div className={cn("h-full", barClassName)} style={{ width: `${pct}%` }} /> : null}
      </div>
      <span className={cn("shrink-0 text-xs", labelClassName)}>{displayText}</span>
    </div>
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHeaderRow,
  TableHeaderCell,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableScrollContainer,
  TableSkeletonRow,
  TableEmptyRow,
  TableGridHeaderRow,
  TableGridHeaderCell,
  TableGridRow,
  TableGridCell,
  TableGridSkeletonRow,
  TableGridEmptyRow,
  MetricBar,
}
